import {
  StoreModule,
  provideMapGetter,
  provideMapState,
  provideMapMutation,
  provideMapAction,
} from '@/scripts/store/helpers/createStore';
import jwplayer from '@/scripts/libs/player/jwplayer-importer';
import { State, VideoPlayerService } from './state';
import { Mutations } from './mutations';
import { Getters } from './getters';
import { Actions } from './actions';
import { setPlayerControl } from './actions/playerControl';
import { initServices, destroyServices } from './actions/services';
import { VisualQuality } from './types/player';
import { exitFullscreen, requestFullscreen } from './utilities/fullscreen';
import { attachEventListeners, detachEventListeners } from './utilities/eventHandler';

export const storeNamespace = 'videoPlayer';

const videoPlayers: Record<string, jwplayer.JWPlayer | undefined> = {};

export const storeModule: StoreModule<State, Getters, Mutations, Actions, any, any> = {
  namespaced: true,
  state: {
    // Player
    playerId: null,

    // States
    state: 'idle',
    // playback
    position: 0,
    duration: 0,
    playbackRate: 1,
    // volume
    volume: 100,
    mute: false,
    // quality
    currentQuality: 0,
    visualQuality: {},
    qualityLevels: [],
    // captions
    captionsList: [],
    currentCaptions: 0,

    // External state
    fullscreen: false,
    castAvailable: false,

    // Others
    viewable: false,

    // Internal state
    // eslint-disable-next-line @typescript-eslint/camelcase
    $_internal: {
      playerHandlerMaps: {},
      externalHandlerMaps: {},
      services: [],
    },

    controls: {
      visibility: {
        controlBarResetHandle: null,
        controlBar: false,
      },
      inPlayerOverlays: [],
    },

    persistence: {
      settings: {
        autoplay: {
          nonCollection: false,
          collection: true,
        },
      },
    },
  },
  mutations: {
    set(state, [key, value]): void {
      (state as any)[key] = value;
    },

    setControl(state, [key, value]): void {
      (state.controls.visibility as any)[key] = value;
    },

    setInPlayerOverlays(state, manifests): void {
      state.controls.inPlayerOverlays = manifests;
    },

    setServices(state, services): void {
      state.$_internal.services = services.map(x => Object.freeze(x));
    },
  },

  getters: {
    player: (state): Getters['player'] => (state.playerId && videoPlayers[state.playerId]) || null,
    service: state => (name): VideoPlayerService | null => state.$_internal.services.find(service => service.name === name) || null,
  },

  actions: {
    async init(context, { el, options }): Promise<void> {
      const { dispatch, commit } = context;
      const player = jwplayer(el).setup(options) as jwplayer.JWPlayer & { id: string };

      videoPlayers[player.id] = player;

      commit('set', ['playerId', player.id]);
      await dispatch('updateStates');
      attachEventListeners(context);
      initServices(context);
    },

    destroy(context): void {
      destroyServices(context);
      detachEventListeners(context);

      const existingPlayerId = context.state.playerId;
      context.commit('set', ['playerId', null]);

      delete videoPlayers[existingPlayerId ?? ''];

      context.getters.player?.remove();
    },

    updateStates({ commit, getters }): void {
      const player = getters.player;
      if (!player) {
        throw new Error('player not available');
      }

      commit('set', ['state', player.getState()]);
      commit('set', ['duration', player.getDuration()]);
      commit('set', ['position', player.getPosition()]);
      commit('set', ['volume', player.getVolume()]);
      commit('set', ['mute', player.getMute()]);

      commit('set', ['currentQuality', player.getCurrentQuality()]);
      commit('set', ['visualQuality', player.getVisualQuality() as VisualQuality]);
      commit('set', ['qualityLevels', player.getQualityLevels()]);

      commit('set', ['captionsList', player.getCaptionsList()]);
      commit('set', ['currentCaptions', player.getCurrentCaptions()]);
      commit('set', ['playbackRate', player.getPlaybackRate()]);
    },

    exitFullscreen(context): void {
      return exitFullscreen(context);
    },
    requestFullscreen(context): void {
      return requestFullscreen(context);
    },

    setPlayerControl(context, payload): void {
      setPlayerControl(context, payload);
    },
  },
};

export const mapState = provideMapState<State>(storeNamespace);
export const mapGetter = provideMapGetter<Getters>(storeNamespace);
export const mapMutation = provideMapMutation<Mutations>(storeNamespace);
export const mapAction = provideMapAction<Actions>(storeNamespace);
